import React from "react";
import { ItemGroupSummary, PickUpPage, PurchaseHistory, OrderLifecycle } from '../component.d';
import { Redirect } from 'react-router-dom';
import { getOrderHistory, getOrderStatus, getPickUpInstructionsPage, getQRString } from '../../accessors/GraphQLAccessor';
import PickupInstructionsLoading from './PickupInstructionsLoading';
import PickupInstructionsPage from './PickupInstructionsPage';
import MissedPickupLoading from '../missedPickup/MissedPickupLoading';
import MissedPickupPage from '../missedPickup/MissedPickupPage';
import PostPickupLoading from '../postPickup/PostPickupLoading';
import PostPickupPage from '../postPickup/PostPickupPage';

import appMetrics from '../../common/MetricsService';
import MetricsConstants from '../../common/MetricsConstants';
import * as KatalMetrics from '@amzn/katal-metrics';
import { AppContext } from "../../AppContext";
import { Cache } from 'aws-amplify/utils';

// Enum to define pickup flow states
enum PickupFlowState {
    PICKUP_INSTRUCTIONS = 'pickup_instructions',
    MISSED_PICKUP = 'missed_pickup',
    POST_PICKUP = 'post_pickup'
}

// Refresh order data every 10 seconds
const ORDER_DATA_REFRESH_INTERVAL = 10000;

class PickupFlowHOC extends React.Component<any, any> {
    actionMetricsPublisher: any;
    attemptMetric: any;
    orderStatusWithQrRefreshInterval: NodeJS.Timeout | null = null;
    lastKnownOrderStatus: OrderLifecycle | null = null;

    constructor(props: any) {
        super(props);
        this.state = {
            pickUpId: null,
            view: this.getLoadingComponent(),
            qrString: null
        };
        this.actionMetricsPublisher = appMetrics.getMetricsPublisherForMethod("PickupFlow");
    }

    determinePickupState(itemGroupSummary: ItemGroupSummary): PickupFlowState {

        switch (itemGroupSummary.orderStatus) {
            case OrderLifecycle.DeliveredYesterday:
                return PickupFlowState.POST_PICKUP;

            case OrderLifecycle.Cancelled:
                // For P0, both user-initiated cancellations and missed pickup initiated cancellations will share the same UX.
                // Namely, https://tiny.amazon.com/11d9y0qrc/MissedPickupUX.
                // In the near future, we'll likely want to read the cancellation reason off the Purchase Doc and determine
                // UX based off that. This will require product input.
                return PickupFlowState.MISSED_PICKUP;

            default:
                return PickupFlowState.PICKUP_INSTRUCTIONS; // Default for active pick-up orders
        }
    }

    getLoadingComponent(flowState?: PickupFlowState) {
        // Use provided flowState, or props flowState, or auto-detected state, or default
        switch (flowState) {
            case PickupFlowState.PICKUP_INSTRUCTIONS:
                return <PickupInstructionsLoading />;
            case PickupFlowState.MISSED_PICKUP:
                return <MissedPickupLoading />;
            case PickupFlowState.POST_PICKUP:
                return <PostPickupLoading />;
            default:
                return <PickupInstructionsLoading />;
        }
    }

    /**
     * This function handles concurrent fetches of order status and the QR code string. It is a suboptimal approach
     * to handling the problem of dynamic QR code updates (i.e., render QR code if the order status has changed to
     * ReadyForPickup) because we are querying order status through the Purchase History resolver, which is overkill.
     * We don't need all the bells and whistles of that resolver (i.e., multiple IDPS Queries + PCE merging). Instead,
     * we'd want a lightweight resolver that performs GET calls on IDPS directly.
     * Tech debt SIM: https://sim.amazon.com/issues/potux-2130
     */
    refreshOrderStatusWithQR = () => {
        if (!this.state.pickupId) return;

        Promise.all([
            getOrderStatus(),
            getQRString(this.state.pickupId)
        ])
        .then(([statusResponse, qrResponse]) => {
            const statusOrderHistory: ItemGroupSummary[] = statusResponse.data.purchaseHistory.upcoming
                .filter((itemGroupSummary: ItemGroupSummary) => itemGroupSummary.id === this.state.pickupId);
            
            if (statusOrderHistory?.length > 0) {
                const newOrderStatus = statusOrderHistory[0].orderStatus;
                const newQRString = qrResponse.data.pickupPage.qrCode.qrString;
                
                // Update if orderStatus changed (triggers getQRStatus re-evaluation)
                if (this.lastKnownOrderStatus !== newOrderStatus) {
                    this.lastKnownOrderStatus = newOrderStatus;
                    
                    // Update the itemGroupSummary with new orderStatus
                    const updatedItemGroupSummary = {
                        ...this.state.itemGroupSummary,
                        orderStatus: newOrderStatus
                    };
                    
                    const updatedPickupPage = {
                        ...this.state.view.props.pickupPage,
                        qrCode: { qrString: newQRString }
                    };
                    
                    this.setState({
                        view: this.getPageComponent(
                            updatedItemGroupSummary,
                            this.state.view.props.purchaseHistory,
                            updatedPickupPage
                        ),
                        qrString: newQRString,
                        itemGroupSummary: updatedItemGroupSummary
                    });
                } else if (this.state.qrString !== newQRString) {
                    // Just update QR if only QR changed
                    this.setState({ qrString: newQRString });
                    this.updatePageWithNewQRString(newQRString);
                }
            }
        })
        .catch(() => {
            // Silent fail
        });
    }

    refreshQRCodeString = () => {
        if (this.state.pickupId) {
            getQRString(this.state.pickupId)
                .then((response: any) => {
                    const newQRString = response.data.pickupPage.qrCode.qrString;
                    if (this.state.qrString !== newQRString) {
                        this.setState({ qrString: newQRString });
                        this.updatePageWithNewQRString(newQRString);
                    }
                })
                .catch((error) => {
                    // TODO: handle QR code sad path
                    // SIM: https://issues.amazon.com/issues/potux-1959
                });
        }
    }

    updatePageWithNewQRString(newQRString: any) {
        if (this.state.view && this.state.view.props) {
            const updatedPickupPage = {
                ...this.state.view.props.pickupPage,
                qrCode: {
                    qrString: newQRString
                }
            };

            this.setState({
                view: this.getPageComponent(
                    this.state.view.props.itemGroupSummary,
                    this.state.view.props.purchaseHistory,
                    updatedPickupPage
                )
            });
        }
    }

    /**
     * Based on order status, determines what "variation" of the pick-up instructions page we'd like to display.
     * @param itemGroupSummary collection of related pick-up orders that share the same facility and pick up window
     * @param purchaseHistory customer's purchase history in AP, excluding items older than 90 days
     * @param pickUpPage data required to render the pick-up page
     */
    getPageComponent(itemGroupSummary: ItemGroupSummary, purchaseHistory: PurchaseHistory, pickUpPage: PickUpPage) {
        // Determine pick-up state based on order status
        const flowState = this.determinePickupState(itemGroupSummary);

        switch (flowState) {
            case PickupFlowState.PICKUP_INSTRUCTIONS:
                return (
                    <PickupInstructionsPage
                        itemGroupSummary={itemGroupSummary}
                        purchaseHistory={purchaseHistory}
                        pickupPage={pickUpPage}
                    />
                );

            case PickupFlowState.MISSED_PICKUP:
                return (
                    <MissedPickupPage
                        itemGroupSummary={itemGroupSummary}
                        purchaseHistory={purchaseHistory}
                        pickupPage={pickUpPage}
                    />
                );

            case PickupFlowState.POST_PICKUP:
                return (
                    <PostPickupPage
                        itemGroupSummary={itemGroupSummary}
                        purchaseHistory={purchaseHistory}
                        pickupPage={pickUpPage}
                    />
                );
        }
    }


    selectItemGroupSummary(itemGroupSummary: ItemGroupSummary, purchaseHistory: PurchaseHistory) {
        // Determine the flow state to show appropriate loading component
        const flowState = this.determinePickupState(itemGroupSummary);

        this.setState({
            view: this.getLoadingComponent(flowState)
        });

        const pickupId = itemGroupSummary.id;

        Cache.setItem("pickupId", pickupId);

        getPickUpInstructionsPage(pickupId)
            .then((response: any) => {
                const pickUpPage = response.data.pickupPage;
                this.setState({
                    view: (this.getPageComponent(itemGroupSummary, purchaseHistory, pickUpPage)),
                    qrString: pickUpPage.qrCode.qrString
                });
                this.attemptMetric.setSuccess();
                this.actionMetricsPublisher.publish(this.attemptMetric);
            })
            .catch(() => {
                this.setState({
                    view: <pui-text data-csa-c-content-id="pickup-page-error" data-csa-c-type="card" data-csa-c-slot-id="pickup">Error</pui-text>,
                });
                this.attemptMetric.setFailure();
                this.actionMetricsPublisher.publish(this.attemptMetric);
            })
        this.setState({ pickupId, itemGroupSummary: itemGroupSummary });
        
        // Set initial order status for comparison
        this.lastKnownOrderStatus = itemGroupSummary.orderStatus;
    }

    getRedirectPath() {
        // Return different redirect paths based on flow state if needed
        return "/your-orders";
    }

    getErrorElementId(detectedState?: PickupFlowState) {
        switch (detectedState) {
            case PickupFlowState.PICKUP_INSTRUCTIONS:
                return "pickup-instructions-error";
            case PickupFlowState.MISSED_PICKUP:
                return "missed-pickup-error";
            case PickupFlowState.POST_PICKUP:
                return "post-pickup-error";
            default:
                return "pickup-flow-error";
        }
    }

    getCsaSlotId(detectedState?: PickupFlowState) {
        switch (detectedState) {
            case PickupFlowState.PICKUP_INSTRUCTIONS:
                return "pickup";
            case PickupFlowState.MISSED_PICKUP:
                return "missed-pickup";
            case PickupFlowState.POST_PICKUP:
                return "post-pickup";
            default:
                return "pickup-flow";
        }
    }

    filterItemGroupSummaryFromPurchaseHistory(pickupId: string | null, orderId: string | null) {
        let orderHistory: ItemGroupSummary[] = []
            .concat(this.context.getPurchaseHistory().upcoming, this.context.getPurchaseHistory().past)
            .filter((itemGroupSummary: ItemGroupSummary) => itemGroupSummary.id === (pickupId ?? ""));

        // If there's an orderId appended to the virtual route, then we're interested in a specific order, as opposed
        // to a group of orders, and therefore we want to find the specific item group summary for that order. This
        // is a solution to address scenarios where pick up orders share the same facility and pick up window, but
        // an order(s) in the group was cancelled / picked up, and the other(s) remain active
        if (orderId) {
            const matchingOrderIdGroup = orderHistory
                .find((itemGroupSummary: ItemGroupSummary) =>
                    itemGroupSummary.itemGroups[0].itemSummaries[0].orderId === orderId
                );

            if (matchingOrderIdGroup) {
                orderHistory = [matchingOrderIdGroup];
            }
        }
        return orderHistory;
    }

    async componentDidMount() {
        this.attemptMetric = new KatalMetrics.Metric.TimedAttempt(MetricsConstants.METRIC_LOAD_PAGE_DATA).withMonitor();

        // Start unified polling every 10 seconds
        this.orderStatusWithQrRefreshInterval = setInterval(this.refreshOrderStatusWithQR, ORDER_DATA_REFRESH_INTERVAL);

        const pickupIdFromUrl = this.props.match.params.pickupId;

        const orderIdFromUrl = this.props.match.params.orderId;

        let pickupId: string | null = null;
        if (pickupIdFromUrl) {
            pickupId = pickupIdFromUrl;
        } else {
            const pickupIdFromCache = await Cache.getItem("pickupId");
            pickupId = pickupIdFromCache;
        }

        let orderHistory =
            this.filterItemGroupSummaryFromPurchaseHistory(pickupId, orderIdFromUrl);

        if (orderHistory && orderHistory.length > 0) {
            this.selectItemGroupSummary(orderHistory[0], this.context.getPurchaseHistory());
        } else {
            // When a customer enters directly to this page, fetch the order history first
            getOrderHistory()
                .then((response: any) => {
                    this.context.addPurchaseHistory(response.data.purchaseHistory);
                    orderHistory = this.filterItemGroupSummaryFromPurchaseHistory(pickupId, orderIdFromUrl);
                    if (orderHistory) {
                        this.selectItemGroupSummary(orderHistory[0], this.context.getPurchaseHistory());
                    } else {
                        const redirectPath = this.getRedirectPath();
                        const csaSlotId = this.getCsaSlotId();
                        this.setState({
                            view: <Redirect
                                data-csa-c-content-id={`${csaSlotId}-redirection-to-order-history`}
                                data-csa-c-type="element"
                                data-csa-c-slot-id={csaSlotId}
                                to={redirectPath}
                            />
                        });
                    };
                    this.attemptMetric.setSuccess();
                    this.actionMetricsPublisher.publish(this.attemptMetric);
                })
                .catch(() => {
                    const errorId = this.getErrorElementId();
                    const csaSlotId = this.getCsaSlotId();
                    this.setState({
                        view: <pui-text
                            id={errorId}
                            data-csa-c-content-id={errorId}
                            data-csa-c-type="element"
                            data-csa-c-slot-id={csaSlotId}
                        >
                            Error
                        </pui-text>,
                    });
                    this.attemptMetric.setFailure();
                    this.actionMetricsPublisher.publish(this.attemptMetric);
                })

        }
    }

    componentWillUnmount() {
        // Clear the order data refresh interval when component unmounts
        if (this.orderStatusWithQrRefreshInterval) {
            clearInterval(this.orderStatusWithQrRefreshInterval);
        }
    }

    render() {
        return (this.state.view);
    }
}

PickupFlowHOC.contextType = AppContext;

export default PickupFlowHOC;
export { PickupFlowState };
